import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, TextField, MenuItem, Table, TableHead, TableBody, TableRow,
  TableCell, TableContainer, Chip, CircularProgress,
} from '@mui/material';
import { BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import apiService from '../services/api';

const COLORS = ['#2563eb', '#16a34a', '#d97706', '#dc2626', '#6c757d'];
const eur = (n: any) =>
  new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(parseFloat(n) || 0);

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <Paper variant="outlined" sx={{ mb: 3 }}>
    <Box sx={{ px: 2.5, py: 1.5, borderBottom: 1, borderColor: 'divider' }}>
      <Typography variant="h6" sx={{ fontSize: '1.05rem' }}>{title}</Typography>
    </Box>
    <Box sx={{ p: 2.5 }}>{children}</Box>
  </Paper>
);

const Statistics: React.FC = () => {
  const [userStats, setUserStats] = useState<any>(null);
  const [endpointStats, setEndpointStats] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('7d');

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const [users, endpoints] = await Promise.all([
          apiService.getUserStats(period),
          apiService.getEndpointStats(period),
        ]);
        setUserStats(users);
        setEndpointStats(endpoints);
      } catch (error) {
        console.error('Error loading statistics:', error);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [period]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, py: 8 }}>
        <CircularProgress /><Typography color="text.secondary">Caricamento statistiche…</Typography>
      </Box>
    );
  }

  const topEndpoints = endpointStats?.endpoints.slice(0, 5) || [];
  const endpointDistribution = topEndpoints.map((e: any) => ({ name: e.endpoint.split('/').pop(), value: e.total_calls }));

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4">Statistiche Dettagliate</Typography>
        <Typography color="text.secondary">Analisi approfondita dell'utilizzo delle API</Typography>
      </Box>

      <TextField select label="Periodo" size="small" sx={{ minWidth: 200, mb: 3 }}
        value={period} onChange={(e) => setPeriod(e.target.value)}>
        <MenuItem value="7d">Ultimi 7 giorni</MenuItem>
        <MenuItem value="30d">Ultimi 30 giorni</MenuItem>
        <MenuItem value="90d">Ultimi 90 giorni</MenuItem>
      </TextField>

      <Section title="Distribuzione Chiamate per Endpoint">
        <ResponsiveContainer width="100%" height={300}>
          <PieChart>
            <Pie data={endpointDistribution} cx="50%" cy="50%" labelLine={false}
              label={({ name, percent }) => `${name}: ${percent ? (percent * 100).toFixed(0) : 0}%`}
              outerRadius={90} dataKey="value">
              {endpointDistribution.map((_: any, index: number) => <Cell key={index} fill={COLORS[index % COLORS.length]} />)}
            </Pie>
            <Tooltip />
          </PieChart>
        </ResponsiveContainer>
      </Section>

      <Section title="Performance Endpoint">
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Endpoint</TableCell>
                <TableCell>Metodo</TableCell>
                <TableCell align="right">Chiamate</TableCell>
                <TableCell align="right">Cache Hit Rate</TableCell>
                <TableCell align="right">Tempo Medio</TableCell>
                <TableCell align="right">Cliente ha pagato</TableCell>
                <TableCell align="right">Costo per noi</TableCell>
                <TableCell align="right">Margine</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {endpointStats?.endpoints.map((e: any, i: number) => (
                <TableRow key={i} hover>
                  <TableCell><code>{e.endpoint}</code></TableCell>
                  <TableCell><Chip size="small" color={e.request_method === 'GET' ? 'success' : 'info'} label={e.request_method} /></TableCell>
                  <TableCell align="right">{e.total_calls}</TableCell>
                  <TableCell align="right" sx={{ color: 'success.main', fontWeight: 600 }}>{e.cache_hit_rate}</TableCell>
                  <TableCell align="right">{parseFloat(e.avg_response_time || 0).toFixed(0)}ms</TableCell>
                  <TableCell align="right">{eur(e.portal_revenue)}</TableCell>
                  <TableCell align="right">{eur(e.upstream_costs)}</TableCell>
                  <TableCell align="right" sx={{ color: 'success.main', fontWeight: 600 }}>{eur(e.profit_margin)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Section>

      <Section title="Tempi di Risposta per Endpoint">
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={topEndpoints}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="endpoint" angle={-45} textAnchor="end" height={100} />
            <YAxis label={{ value: 'ms', angle: -90, position: 'insideLeft' }} />
            <Tooltip />
            <Legend />
            <Bar dataKey="avg_response_time" fill="#2563eb" name="Tempo Medio (ms)" />
            <Bar dataKey="min_response_time" fill="#16a34a" name="Tempo Minimo (ms)" />
            <Bar dataKey="max_response_time" fill="#dc2626" name="Tempo Massimo (ms)" />
          </BarChart>
        </ResponsiveContainer>
      </Section>

      <Section title="Statistiche Utenti">
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>User Reference</TableCell>
                <TableCell align="right">Chiamate</TableCell>
                <TableCell align="right">Riuscite</TableCell>
                <TableCell align="right">Tempo Medio</TableCell>
                <TableCell align="right">Cliente ha pagato</TableCell>
                <TableCell>Ultima Chiamata</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {userStats?.users.map((u: any, i: number) => (
                <TableRow key={i} hover>
                  <TableCell><strong>{u.user_reference || 'N/A'}</strong></TableCell>
                  <TableCell align="right">{u.total_calls}</TableCell>
                  <TableCell align="right">{u.successful_calls}</TableCell>
                  <TableCell align="right">{parseFloat(u.avg_response_time || 0).toFixed(0)}ms</TableCell>
                  <TableCell align="right">{eur(u.total_cost)}</TableCell>
                  <TableCell>{u.last_call ? new Date(u.last_call).toLocaleString('it-IT') : '—'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Section>
    </Box>
  );
};

export default Statistics;
